import React from 'react';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { t } from '../../../utils/textUtils';
import { computeTotalProgressPercent } from '../utils/formatters';

const ChartSection = ({ 
  isProjectMode, 
  isUnitMode, 
  dataByUnit, 
  dataByCategory, 
  unitOverviewData 
}) => {
  const chartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        grid: { color: 'rgba(0,0,0,0.04)', drawBorder: false },
        ticks: {
          callback: (value) => value + '%',
          color: '#94A3B8',
          font: { size: 11 }
        }
      },
      y: {
        grid: { display: false },
        ticks: {
          autoSkip: false,
          color: '#475569',
          font: { size: 11, weight: '500' }
        }
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0f172a',
        titleColor: '#fff',
        bodyColor: '#94A3B8',
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (context) => `  Progress: ${context.raw.toFixed(2)}%`
        }
      }
    }
  };

  const chartData = isProjectMode
    ? {
        labels: dataByUnit.map((item) => {
          const label = item.moduleName || item.modularUnitName || '';
          // Show full building name without truncation
          return label;
        }),
        datasets: [{
          label: '% Complete',
          data: dataByUnit.map(item => computeTotalProgressPercent(item)),
          backgroundColor: dataByUnit.map(item => {
            const progress = computeTotalProgressPercent(item);
            if (progress >= 80) return '#22C55E';
            if (progress >= 50) return '#F59E0B';
            return '#EF4444';
          }),
          borderRadius: 6,
          barThickness: 28,
        }],
      }
    : {
        labels: (isUnitMode ? unitOverviewData.progressPerKategori : dataByCategory).map(item => {
          const label = item.kategori || item.workCategoryName || item.name;
          // Don't truncate in project mode to avoid duplicate labels for same category across units
          if (!isUnitMode && label.length > 25) {
            return label; // Show full label for project mode (unit - category)
          }
          return label.length > 25 ? `${label.substring(0, 25)}...` : label;
        }),
        datasets: [{
          label: '% Complete',
          data: (isUnitMode ? unitOverviewData.progressPerKategori : dataByCategory).map(item => isUnitMode ? item.progress : computeTotalProgressPercent(item)),
          backgroundColor: (isUnitMode ? unitOverviewData.progressPerKategori : dataByCategory).map(item => {
            const progress = isUnitMode ? item.progress : computeTotalProgressPercent(item);
            if (progress >= 80) return '#22C55E';
            if (progress >= 50) return '#F59E0B';
            return '#EF4444';
          }),
          borderRadius: 6,
          barThickness: 28,
        }],
      };

  const itemCount = isProjectMode ? dataByUnit.length : (isUnitMode ? unitOverviewData.progressPerKategori?.length : dataByCategory.length) || 0;
  const dynamicHeight = Math.max(300, itemCount * 44);

  return (
    <Card 
      sx={{ 
        mb: 3, 
        border: '1px solid #E2E8F0', 
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        px: 3, py: 2,
        borderBottom: '1px solid #F1F5F9',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: '#FAFBFC'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 2,
            background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(99,102,241,0.3)'
          }}>
            <Typography sx={{ color: '#fff', fontSize: 16 }}>📊</Typography>
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, lineHeight: 1.2 }}>
              {isProjectMode ? t('PROGRESS PER BANGUNAN') : t('PROGRESS PER KATEGORI')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
              {isProjectMode ? 'Perbandingan kemajuan antar bangunan' : 'Perbandingan kemajuan antar kategori'}
            </Typography>
          </Box>
        </Box>
        <Chip 
          label={`${itemCount} ${itemCount === 1 ? 'Item' : 'Items'}`}
          size="small"
          sx={{ 
            bgcolor: '#EEF2FF',
            color: '#6366F1',
            fontWeight: 700,
            border: '1px solid #C7D2FE'
          }}
        />
      </Box>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ height: dynamicHeight }}>
          <Bar data={chartData} options={chartOptions} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default ChartSection;
